import { useEffect, useMemo, useState } from 'react';
import { Check, ChevronLeft, ChevronRight, Copy, ImageOff, Loader2, Plus, Sparkles, X } from 'lucide-react';
import { UploadedFoto } from '@/services/api';
import { toAbsoluteFotoUrl, withPreviewParams } from '@/utils/foto-url';
import { useI18n } from '@/contexts/i18n-hook';

interface FotosCampoGaleriaProps {
  fotos: UploadedFoto[];
  /** Quando informado, é possível adicionar a foto (ex.: a um slot da parte).
   *  Recebe a URL relativa da foto (`/uploads/...`). */
  onSelecionar?: (url: string) => void | Promise<void>;
  /** Texto do botão/feedback de ação ao selecionar. Sem valor, cai em `comum.acao.adicionar`. */
  rotuloSelecionar?: string;
  /** Tamanho das miniaturas. */
  compact?: boolean;
  /** URLs já usadas em partes — essas fotos ficam marcadas e vão para o final. */
  usadasUrls?: string[];
}

/** Normaliza uma URL para comparação (sem origem, sem query, decodificada). */
function normalizarUrl(u: string): string {
  if (!u) return '';
  let s = u;
  const idx = s.indexOf('/uploads/');
  if (idx >= 0) s = s.slice(idx);
  s = s.split('?')[0];
  try {
    s = decodeURIComponent(s);
  } catch {
    /* mantém */
  }
  return s;
}

/**
 * Galeria das fotos de campo vinculadas a uma apreciação.
 * Clicar numa foto abre um visualizador ampliado com navegação (setas/teclado);
 * a seleção/cópia é feita dentro do visualizador (ou pelos botões da miniatura).
 */
export function FotosCampoGaleria({ fotos, onSelecionar, rotuloSelecionar, compact, usadasUrls }: FotosCampoGaleriaProps) {
  const { t } = useI18n();
  const rotuloSelecionarEfetivo = rotuloSelecionar ?? t('comum.acao.adicionar');
  const [viewerIdx, setViewerIdx] = useState<number | null>(null);
  const [copiando, setCopiando] = useState<string | null>(null);
  const [copiada, setCopiada] = useState<string | null>(null);
  const [processando, setProcessando] = useState<string | null>(null);
  const [adicionada, setAdicionada] = useState<string | null>(null);

  const usadasSet = useMemo(() => new Set((usadasUrls || []).map(normalizarUrl)), [usadasUrls]);
  const estaUsada = (foto: UploadedFoto) => usadasSet.has(normalizarUrl(foto.url));

  // Não-usadas primeiro; usadas vão para o final (mantendo a ordem original).
  const fotosOrdenadas = useMemo(() => {
    if (usadasSet.size === 0) return fotos;
    const naoUsadas: UploadedFoto[] = [];
    const usadas: UploadedFoto[] = [];
    for (const f of fotos) (usadasSet.has(normalizarUrl(f.url)) ? usadas : naoUsadas).push(f);
    return [...naoUsadas, ...usadas];
  }, [fotos, usadasSet]);

  const total = fotosOrdenadas.length;
  const navegar = (delta: number) =>
    setViewerIdx((i) => (i === null ? i : (i + delta + total) % total));

  // Navegação por teclado quando o visualizador está aberto.
  useEffect(() => {
    if (viewerIdx === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        e.stopPropagation();
        navegar(1);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        e.stopPropagation();
        navegar(-1);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        setViewerIdx(null);
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewerIdx, total]);

  if (!fotos || fotos.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 text-sm text-schmersal-gray-medium py-8">
        <ImageOff className="h-6 w-6" />
        {t('apreciacaoModais.fotosCampo.vazio')}
      </div>
    );
  }

  const copiarImagem = async (foto: UploadedFoto) => {
    const url = toAbsoluteFotoUrl(foto.url);
    setCopiando(foto.url);
    try {
      const resp = await fetch(url);
      const blob = await resp.blob();
      // A maioria dos navegadores só aceita image/png na área de transferência.
      const bitmap = await createImageBitmap(blob);
      const canvas = document.createElement('canvas');
      canvas.width = bitmap.width;
      canvas.height = bitmap.height;
      canvas.getContext('2d')?.drawImage(bitmap, 0, 0);
      const pngBlob: Blob | null = await new Promise((res) => canvas.toBlob(res, 'image/png'));
      if (pngBlob && 'clipboard' in navigator && 'write' in navigator.clipboard) {
        await navigator.clipboard.write([new ClipboardItem({ 'image/png': pngBlob })]);
        setCopiada(foto.url);
        setTimeout(() => setCopiada((c) => (c === foto.url ? null : c)), 1500);
      } else {
        throw new Error('Clipboard de imagem indisponível');
      }
    } catch {
      // Fallback: copiar a URL como texto.
      try {
        await navigator.clipboard.writeText(url);
        setCopiada(foto.url);
        setTimeout(() => setCopiada((c) => (c === foto.url ? null : c)), 1500);
      } catch {
        /* ignore */
      }
    } finally {
      setCopiando(null);
    }
  };

  const selecionar = async (foto: UploadedFoto, avancar = false) => {
    if (!onSelecionar) return;
    setProcessando(foto.url);
    try {
      await onSelecionar(foto.url);
      setAdicionada(foto.url);
      setTimeout(() => setAdicionada((a) => (a === foto.url ? null : a)), 1500);
      if (avancar) navegar(1);
    } finally {
      setProcessando(null);
    }
  };

  const thumbW = compact ? 220 : 320;
  const atual = viewerIdx !== null ? fotosOrdenadas[viewerIdx] : null;

  return (
    <>
      <div className={`grid gap-2 ${compact ? 'grid-cols-2' : 'grid-cols-2 sm:grid-cols-3'}`}>
        {fotosOrdenadas.map((foto, idx) => {
          const usada = estaUsada(foto);
          const analisada = !!foto.descricaoIA;
          return (
            <div key={foto.url} className="relative group">
              <div
                className={`relative border rounded-lg overflow-hidden bg-schmersal-gray-light/40 ${
                  usada
                    ? 'border-amber-400'
                    : analisada
                    ? 'border-purple-400 dark:border-purple-600'
                    : 'border-schmersal-gray-light dark:border-schmersal-gray-dark'
                }`}
              >
                <img
                  src={withPreviewParams(toAbsoluteFotoUrl(foto.previewUrl), thumbW, 55)}
                  alt={foto.nomeArquivo}
                  loading="lazy"
                  onClick={() => setViewerIdx(idx)}
                  className={`w-full h-28 object-cover cursor-zoom-in ${usada ? 'opacity-60' : ''}`}
                  title={
                    analisada
                      ? `${foto.rotuloIA ? `${foto.rotuloIA}\n\n` : ''}${foto.descricaoIA}`
                      : t('apreciacaoModais.fotosCampo.ampliarParaAnalisar')
                  }
                />

                {/* Selos (canto superior esquerdo, empilhados) */}
                <div className="absolute top-1 left-1 flex flex-col items-start gap-1">
                  {usada && (
                    <span className="inline-flex items-center gap-1 bg-amber-500 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded shadow">
                      <Check className="h-3 w-3" /> {t('apreciacaoModais.fotosCampo.usada')}
                    </span>
                  )}
                  {analisada && (
                    <span className="inline-flex items-center gap-1 bg-purple-600 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded shadow">
                      <Sparkles className="h-3 w-3" /> {t('apreciacaoModais.fotosCampo.analisada')}
                    </span>
                  )}
                </div>

                {/* Ação rápida no hover (a análise principal é no visualizador) */}
                {onSelecionar ? (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      selecionar(foto);
                    }}
                    disabled={processando === foto.url}
                    className="absolute inset-x-0 bottom-0 flex items-center justify-center gap-1 bg-schmersal-blue/90 text-white text-xs py-1 opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-100"
                  >
                    {processando === foto.url ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : adicionada === foto.url ? (
                      <Check className="h-3.5 w-3.5" />
                    ) : (
                      <Plus className="h-3.5 w-3.5" />
                    )}
                    {adicionada === foto.url ? t('apreciacaoModais.fotosCampo.adicionada') : rotuloSelecionarEfetivo}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      copiarImagem(foto);
                    }}
                    disabled={copiando === foto.url}
                    className="absolute top-1.5 right-1.5 inline-flex items-center gap-1 bg-white/90 text-schmersal-gray-dark rounded px-1.5 py-1 text-xs shadow opacity-0 group-hover:opacity-100 transition-opacity"
                    title={t('apreciacaoModais.fotosCampo.copiarTitle')}
                  >
                    {copiando === foto.url ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : copiada === foto.url ? (
                      <Check className="h-3.5 w-3.5 text-green-600" />
                    ) : (
                      <Copy className="h-3.5 w-3.5" />
                    )}
                    {copiada === foto.url ? t('apreciacaoModais.fotosCampo.copiada') : t('comum.acao.copiar')}
                  </button>
                )}
              </div>

              {/* Tooltip com a descrição da IA (aparece no hover) — fora do
                  container com overflow-hidden para não ser cortado. */}
              {analisada && (
                <div className="absolute left-0 right-0 top-full z-40 mt-1 hidden group-hover:block">
                  <div className="rounded-md border border-purple-300 dark:border-purple-700 bg-white dark:bg-schmersal-gray-darkest shadow-xl p-2 text-xs text-schmersal-gray-dark dark:text-white max-h-56 overflow-auto">
                    {foto.rotuloIA && (
                      <p className="font-semibold text-purple-700 dark:text-purple-300 mb-1 flex items-center gap-1">
                        <Sparkles className="h-3 w-3 shrink-0" /> {foto.rotuloIA}
                      </p>
                    )}
                    <p className="whitespace-pre-wrap leading-snug">{foto.descricaoIA}</p>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Visualizador ampliado com navegação */}
      {atual && viewerIdx !== null && (
        <div
          className="fixed inset-0 z-[70] bg-black/90 flex flex-col"
          onClick={() => setViewerIdx(null)}
        >
          {/* Barra superior */}
          <div className="flex items-center justify-between p-3 text-white" onClick={(e) => e.stopPropagation()}>
            <span className="text-sm">
              {viewerIdx + 1} / {total}
              {estaUsada(atual) && (
                <span className="ml-2 inline-flex items-center gap-1 bg-amber-500 text-white text-[11px] font-semibold px-1.5 py-0.5 rounded">
                  <Check className="h-3 w-3" /> {t('apreciacaoModais.fotosCampo.jaUsada')}
                </span>
              )}
            </span>
            <button
              type="button"
              onClick={() => setViewerIdx(null)}
              className="p-2 rounded bg-white/15 hover:bg-white/25"
              title={t('apreciacaoModais.fotosCampo.fecharTitle')}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Imagem + setas */}
          <div className="flex-1 relative flex items-center justify-center px-12 pb-2 min-h-0" onClick={() => setViewerIdx(null)}>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                navegar(-1);
              }}
              className="absolute left-3 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/15 hover:bg-white/30 text-white"
              title={t('apreciacaoModais.fotosCampo.anteriorTitle')}
            >
              <ChevronLeft className="h-6 w-6" />
            </button>

            <img
              src={toAbsoluteFotoUrl(atual.url)}
              alt={atual.nomeArquivo}
              className="max-w-full max-h-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />

            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                navegar(1);
              }}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/15 hover:bg-white/30 text-white"
              title={t('apreciacaoModais.fotosCampo.proximaTitle')}
            >
              <ChevronRight className="h-6 w-6" />
            </button>
          </div>

          {/* Descrição gerada pela IA (quando a foto já foi analisada) */}
          {atual.descricaoIA && (
            <div className="px-4 pb-1" onClick={(e) => e.stopPropagation()}>
              <div className="mx-auto max-w-3xl rounded-lg bg-white/10 text-white text-sm p-3 max-h-40 overflow-auto">
                {atual.rotuloIA && (
                  <p className="font-semibold text-purple-200 mb-1 flex items-center gap-1">
                    <Sparkles className="h-4 w-4 shrink-0" /> {atual.rotuloIA}
                  </p>
                )}
                <p className="whitespace-pre-wrap leading-snug">{atual.descricaoIA}</p>
              </div>
            </div>
          )}

          {/* Ações */}
          <div className="flex items-center justify-center gap-3 p-4" onClick={(e) => e.stopPropagation()}>
            {onSelecionar ? (
              <button
                type="button"
                onClick={() => selecionar(atual, true)}
                disabled={processando === atual.url}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-schmersal-blue text-white font-medium hover:bg-schmersal-blue-dark disabled:opacity-60"
              >
                {processando === atual.url ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : adicionada === atual.url ? (
                  <Check className="h-4 w-4" />
                ) : (
                  <Plus className="h-4 w-4" />
                )}
                {adicionada === atual.url ? t('apreciacaoModais.fotosCampo.adicionadaConfirmacao') : t('apreciacaoModais.fotosCampo.selecionarFoto')}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => copiarImagem(atual)}
                disabled={copiando === atual.url}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-white text-schmersal-gray-dark font-medium hover:bg-gray-100 disabled:opacity-60"
              >
                {copiando === atual.url ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : copiada === atual.url ? (
                  <Check className="h-4 w-4 text-green-600" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
                {copiada === atual.url ? t('apreciacaoModais.fotosCampo.copiadaConfirmacao') : t('apreciacaoModais.fotosCampo.copiarImagem')}
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
}
